import React, { useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from 'react-redux';
import * as yup from "yup";
import { Button, MCard, TippedIcon } from "components/Shared";
import OrderStepButtons from "components/WebOrderV2/OrderStepButtons";
import { LENDER, COMMERCIAL } from "typeConstants/webOrder";
import {
  DATA_TYPE_DROPDOWN2,
  DATA_TYPE_BOOLEAN,
  DATA_TYPE_PHONE_NUMBER,
  DATA_TYPE_EMAIL,
  DATA_TYPE_MULTI_LINE_STRING,
} from "typeConstants/dataTypes";
import responseHandler from 'responseHandler';
import { contactRoleOptions, occupancyOptions, commercialContactRoleOptions } from "components/WebOrderV2/dropdownOptions";

const Contact = props => {
  const mCardControlMethodsContact = useRef({});
  const mCardControlMethodsAdditional = useRef({});
  const mounted = useRef(true);

  useEffect(() => {
    return () => {
      mounted.current = false;
    };
  }, []);

  const setControlMethodsContact = () => {
    return cm => (mCardControlMethodsContact.current = cm);
  };

  const setControlMethodsAdditional = () => {
    return cm => (mCardControlMethodsAdditional.current = cm);
  };

  const onPrevious = () => {
    props.onPrevious(getDataWithDropdownOptions());
  };

  const onNext = () => {
    let contactIsValid = props.handlingFeatures?.isAppointmentRequired ? mCardControlMethodsContact.current.runValidation(true) : true;
    let additionalIsValid = mCardControlMethodsAdditional.current.runValidation(true);
    if (contactIsValid && additionalIsValid) {
      props.onNext(getDataWithDropdownOptions());
    }
  };

  const getDataWithDropdownOptions = () => {
    let editedContact = props.handlingFeatures?.isAppointmentRequired ? mCardControlMethodsContact.current.getEditedDataObject() : {};
    let editedAdditional = mCardControlMethodsAdditional.current.getEditedDataObject();
    let editedData = { ...editedContact, ...editedAdditional };
    if (editedData.contactRole) {
      const options = props.pageLayout !== COMMERCIAL ? contactRoleOptions : commercialContactRoleOptions;
      let contactOption = options.filter(c => c.value === editedData.contactRole)[0];
      if (contactOption) editedData.contactRole = contactOption;
      // option might not be in the dropdown, so we just make an option for it so the logic that relies on this later works
      else editedData.contactRole = { label: editedData.contactRole, value: editedData.contactRole };
    }
    if (editedData.occupancy) {
      editedData.occupancy = occupancyOptions.filter(o => o.value === editedData.occupancy)[0];
    }
    return editedData;
  };

  let homeownerTitle = props.terminology === LENDER ? "Borrower/Owner" : "Insured";
  let homeownerTitleForContactField = props.terminology === LENDER ? "Borrower" : "Insured";

  // If the user hides these fields, then navigates away,
  // then returns and unhides them, MCard won't have edit data
  // so we might as well just manage them outside of MCard...
  const contactDetailsFields = [
    {
      key: "contactFirstName",
      label: "First Name",
      value: props.data.contactFirstName,
      validation: yup.string().required("Contact first name is required"),
      placeholder: "Required",
    },
    {
      key: "contactLastName",
      label: "Last Name",
      value: props.data.contactLastName,
      validation: yup.string().required("Contact last name is required"),
      placeholder: "Required",
    },
    {
      key: "contactRole",
      label: "Role",
      value: props.data.contactRole,
      dataType: DATA_TYPE_DROPDOWN2,
      dataTypeOptions: {
        options: props.pageLayout !== COMMERCIAL ? contactRoleOptions : commercialContactRoleOptions
      },
      validation: yup.string().nullable().required("Contact role is required"),
      placeholder: "Select... (required)",
    },
    {
      key: "contactPhoneNumber1",
      label: "Phone 1",
      value: props.data.contactPhoneNumber1,
      dataType: DATA_TYPE_PHONE_NUMBER,
      validation: yup.string().required("Contact phone number is required"),
      placeholder: "Required",
    },
    {
      key: "contactPhoneNumber2",
      label: "Phone 2",
      value: props.data.contactPhoneNumber2,
      dataType: DATA_TYPE_PHONE_NUMBER,
    },
    {
      key: "contactEmailAddress",
      label: "Email Address",
      value: props.data.contactEmailAddress,
      dataType: DATA_TYPE_EMAIL,
      validation: yup.string().email("Email must be valid"),
    },
  ];

  const getAdditionalDetailsFields = () => {
    let fields = [];

    if (props.pageLayout !== COMMERCIAL) {
      fields.push(
        {
          key: "occupancy",
          label: "Occupancy",
          value: props.data.occupancy,
          dataType: DATA_TYPE_DROPDOWN2,
          dataTypeOptions: {
            options: occupancyOptions
          }
        }
      );
    }

    if (props.handlingFeatures?.repairNotesRequired) {
      fields.push({
        key: "repairNotes",
        label: <>List Repairs to be Documented<TippedIcon
          iconName="info"
          tooltipText={"e.g., Main floor 1/2 bath exposed electrical wiring."}
          iconClassName={"no-opt-in-edits"}
          tooltipPopoutDirection="bottom"
        /></>,
        value: props.data.repairNotes,
        dataType: DATA_TYPE_MULTI_LINE_STRING,
        validation: yup.string().max(500, "Repair notes cannot exceed 500 characters").required("Required for the inspection only completion report"),
        placeholder: "Required",
      });
    }
    fields.push({
      key: "additionalInfo",
      label: <>Special Order Requests<TippedIcon
        iconName="info"
        tooltipText="Access information, additional contact information, special order requests, notes for Mueller Inspector."
        iconClassName="no-opt-in-edits"
        tooltipPopoutDirection="bottom"
      /></>,
      value: props.data.additionalInfo,
      dataType: DATA_TYPE_MULTI_LINE_STRING,
      validation: yup.string().max(500, "Special Order Requests cannot exceed 500 characters"),
    });

    fields.push({
      key: "rush",
      label: "Rush",
      value: props.data.rush,
      dataType: DATA_TYPE_BOOLEAN,
    });

    return fields;
  };

  const useHomeownerAsContact = () => {
    mCardControlMethodsContact.current.setEditData("contactFirstName", props.data.homeownerFirstName);
    mCardControlMethodsContact.current.setEditData("contactLastName", props.data.homeownerLastName);
    mCardControlMethodsContact.current.setEditData("contactRole", homeownerTitleForContactField);
    mCardControlMethodsContact.current.setEditData("contactPhoneNumber1", props.data.homeownerPhoneNumber1);
    mCardControlMethodsContact.current.setEditData("contactPhoneNumber2", props.data.homeownerPhoneNumber2);
    mCardControlMethodsContact.current.setEditData("contactEmailAddress", props.data.homeownerEmailAddress);
  };

  const getContactFields = () => {
    if(props.pageLayout === COMMERCIAL) { // hide the `Use ${homeownerTitle} as contact` button
      return contactDetailsFields;
    }
    else {
      return [
        {
          key: "useHomeownerAsContact",
          control: <Button style={{ margin: "8px 0" }} onClick={useHomeownerAsContact}>{`Use ${homeownerTitle} as contact`}</Button>,
        },
        ...contactDetailsFields,
      ];
    }
  };

  return (
    <>
      {props.handlingFeatures?.isAppointmentRequired && <MCard
        title="Contact"
        getControlMethods={setControlMethodsContact("contact")}
        fields={getContactFields()}
        onlyEditable
      />}
      <MCard
        title="Additional Info"
        getControlMethods={setControlMethodsAdditional("additional")}
        fields={getAdditionalDetailsFields()}
        onlyEditable
      />
      <OrderStepButtons
        currentStep={props.currentStep}
        totalSteps={props.totalSteps}
        nextTitle={props.currentStep >= props.totalSteps ? "Place Order" : "Next"}
        onPrevious={onPrevious}
        onNext={onNext}
      />
    </>
  );
};

Contact.propTypes = {
  currentStep: PropTypes.number,  // Current step in order process, needed for OrderStepButtons
  data: PropTypes.object, // Data for MCard fields
  onNext: PropTypes.func, // Func to call when fields are valid to move to the next step
  onPrevious: PropTypes.func, // Func to call to move to previous step
  terminology: PropTypes.string, // Insurance or Lender, see typeConstants/webOrder.js
  pageLayout: PropTypes.string, // LenderAppraisal, LenderNonAppraisal, Insurance, Commercial see typeConstants/webOrder.js
  accountFeatures: PropTypes.object, // Enabled customer features for selected account
  handlingFeatures: PropTypes.object,  // caseHandlingFeatures from settings
  makeToast: PropTypes.func, //
};

const mapDispatchToProps = dispatch => ({
  makeToast(text, type) {
    responseHandler.customToast(text, type, dispatch);
  }
});

export default connect(null, mapDispatchToProps)(Contact);
